// =============================================================
// Agenda app — book a training slot at A22 or M15.
//
// Default model: every hour from 07:00 to 20:00 every day is
// bookable. Admin-set events (kind=open|event|closed|taken) act
// as overrides on top. A logged-in member can tap any hour to
// request a booking — the request lives in the DB and the
// admin approves/declines.
//
// All untrusted content (event titles, booking notes, user input
// from the API) is rendered via `textContent` / `createElement` —
// never innerHTML, so XSS is impossible by construction.
// =============================================================

import { t, getLocale, onLocaleChange } from './i18n';
import { fetchAgendaEvents, fetchTakenSlots, fetchMyBookings, createBooking, cancelBooking, getCachedUser, fetchSessionTypesForMember } from './userApi';
import type { AgendaEvent, Booking } from './content';

const HOUR_START = 7;   // 07:00 inclusive
const HOUR_END = 21;    // last slot starts at 20:00 → ends 21:00
const HOUR_COUNT = HOUR_END - HOUR_START;

type SlotStatus = 'open' | 'event' | 'closed' | 'taken' | 'pending' | 'mine';

interface SlotCell {
  startsAt: string;
  endsAt: string;
  status: SlotStatus;
  title?: string;
  eventId?: string;
  bookingId?: string;
}

interface AppState {
  weekStart: Date;
  location: 'A22' | 'M15';
  events: AgendaEvent[];
  takenSlots: AgendaEvent[];
  bookings: Booking[];
  user: ReturnType<typeof getCachedUser>;
}

const state: AppState = {
  weekStart: startOfWeek(new Date()),
  location: 'A22',
  events: [],
  takenSlots: [],
  bookings: [],
  user: null,
};

// ---- date helpers ----
function startOfWeek(d: Date): Date {
  const out = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  const day = (out.getDay() + 6) % 7; // Monday = 0
  out.setDate(out.getDate() - day);
  return out;
}

function addDays(d: Date, n: number): Date {
  const out = new Date(d);
  out.setDate(out.getDate() + n);
  return out;
}

function pad(n: number): string { return n < 10 ? '0' + n : '' + n; }
function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}
function timeInPast(iso: string): boolean {
  return new Date(iso).getTime() <= Date.now();
}

function dayNameKey(i: number): 'mon' | 'tue' | 'wed' | 'thu' | 'fri' | 'sat' | 'sun' {
  return (['mon','tue','wed','thu','fri','sat','sun'] as const)[i];
}

/**
 * Compute the start instant (ms) of the LAST occurrence in a recurring
 * series, mirroring the backend's day-by-day walk. Used to render the
 * "first – last" date range in the modal summary.
 */
function lastOccurrenceMs(startDate: Date, weekdays: number[], weeks: number): number {
  const startMs = startDate.getTime();
  const seedDow = (startDate.getDay() + 6) % 7;
  const dowSet = new Set<number>(weekdays.length ? weekdays : [seedDow]);
  // Look ahead far enough that any week × weekday combo lands: (weeks-1) full
  // weeks of stride + 7 extra days to span the last weekday.
  const horizon = Math.max(1, weeks) * 7 + 7;
  let lastMs = startMs;
  for (let off = 0; off < horizon; off++) {
    const s = new Date(startMs + off * 86_400_000);
    const dow = (s.getDay() + 6) % 7;
    if (!dowSet.has(dow)) continue;
    // Only count occurrences that fall within the requested N weeks.
    const weeksFromStart = Math.floor(off / 7);
    if (weeksFromStart >= weeks) continue;
    lastMs = s.getTime();
  }
  return lastMs;
}

function monthKey(m: number): 'jan' | 'feb' | 'mar' | 'apr' | 'may' | 'jun' | 'jul' | 'aug' | 'sep' | 'oct' | 'nov' | 'dec' {
  return (['jan','feb','mar','apr','may','jun','jul','aug','sep','oct','nov','dec'] as const)[m];
}

function formatDateTime(d: Date, locale: 'hu' | 'en'): string {
  return d.toLocaleString(locale, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
}

function formatRangeLabel(weekStart: Date, locale: 'hu' | 'en'): string {
  const end = addDays(weekStart, 6);
  const dayFmt = (d: Date) => locale === 'en'
    ? `${t(`agendaApp.months.${monthKey(d.getMonth())}`)} ${d.getDate()}`
    : `${t(`agendaApp.months.${monthKey(d.getMonth())}`)} ${d.getDate()}.`;
  if (weekStart.getMonth() === end.getMonth()) {
    return `${dayFmt(weekStart)}–${end.getDate()}, ${weekStart.getFullYear()}`;
  }
  return `${dayFmt(weekStart)} – ${dayFmt(end)}, ${weekStart.getFullYear()}`;
}

/** Format a HUF amount with the Hungarian thousands separator and a `Ft`
 *  suffix. Renders `12000` → `12 000 Ft`. */
function formatHuf(amount: number): string {
  // Hungarian locale uses non-breaking space as the thousands sep;
  // regular space is fine for HTML but we keep it simple.
  const grouped = Math.round(amount).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  return `${grouped} Ft`;
}

// ---- slot classification ----
function slotAt(dayIdx: number, hourIdx: number) {
  const date = addDays(state.weekStart, dayIdx);
  const slotStart = new Date(date);
  slotStart.setHours(HOUR_START + hourIdx, 0, 0, 0);
  const slotEnd = new Date(slotStart.getTime() + 60 * 60 * 1000);
  return { slotStart, slotEnd };
}

function classify(dayIdx: number, hourIdx: number): SlotCell {
  const { slotStart, slotEnd } = slotAt(dayIdx, hourIdx);

  // Priority order (highest first):
  //   1. The current user's own bookings — these must always render
  //      as `mine` / `pending`, never as a generic `taken` chip that
  //      another user might click.
  //   2. Other people's approved bookings — pulled from the public
  //      availability endpoint so trainees see slots that are
  //      effectively unavailable to them.
  //   3. Admin-defined events (taken / closed / event).
  //   4. Past hours → closed.
  //   5. Default → open.

  // 1. The caller's own bookings (across all weeks they have data
  //    for). We check this BEFORE the public-taken list so that even
  //    if the public endpoint mistakenly includes the user's own
  //    booking, the `mine` chip wins.
  //
  //    Recurring bookings only carry the FIRST occurrence on the
  //    parent `startsAt/endsAt` fields; later occurrences live on
  //    `booking.occurrences[]`. We need to match against either,
  //    otherwise subsequent Tuesdays would render as `taken` to the
  //    very member who booked them — the same symptom as the admin
  //    bug, just scoped to one user.
  const slotTs = slotStart.getTime();
  const slotEndTs = slotEnd.getTime();
  const ownBooking = state.bookings.find(b => {
    if (b.location !== state.location) return false;
    const allOccurrences = Array.isArray(b.occurrences) && b.occurrences.length > 0
      ? b.occurrences
      : [{ startsAt: b.startsAt, endsAt: b.endsAt, status: b.status }];
    return allOccurrences.some(o => {
      const oStart = new Date(o.startsAt).getTime();
      const oEnd = new Date(o.endsAt).getTime();
      return oStart <= slotTs && oEnd >= slotEndTs;
    });
  });
  if (ownBooking) {
    // Determine this slot's status: prefer the per-occurrence status
    // if it exists (handles the "first 5 approved, rest still
    // pending" case), otherwise fall back to the parent status.
    let occStatus: string = ownBooking.status;
    if (Array.isArray(ownBooking.occurrences) && ownBooking.occurrences.length > 0) {
      const hit = ownBooking.occurrences.find(o => {
        const oStart = new Date(o.startsAt).getTime();
        const oEnd = new Date(o.endsAt).getTime();
        return oStart <= slotTs && oEnd >= slotEndTs;
      });
      if (hit) occStatus = hit.status;
    }
    let status: SlotStatus;
    if (occStatus === 'approved') status = 'mine';
    else if (occStatus === 'pending') status = 'pending';
    else status = 'open';
    return {
      startsAt: slotStart.toISOString(),
      endsAt: slotEnd.toISOString(),
      status,
      bookingId: ownBooking.id,
    };
  }

  // 2. Public-taken slots (other people's approved bookings).
  const takenSlot = state.takenSlots.find(t => {
    if (t.location !== state.location) return false;
    const tStart = new Date(t.startsAt).getTime();
    const tEnd = new Date(t.endsAt).getTime();
    return tStart <= slotStart.getTime() && tEnd >= slotEnd.getTime();
  });
  if (takenSlot) {
    return {
      startsAt: slotStart.toISOString(),
      endsAt: slotEnd.toISOString(),
      status: 'taken',
      title: takenSlot.title[getLocale()] || takenSlot.title.hu,
      eventId: takenSlot.id,
    };
  }

  // 3. Admin-set events.
  const event = state.events.find(ev => {
    if (ev.location !== state.location) return false;
    const evStart = new Date(ev.startsAt).getTime();
    const evEnd = new Date(ev.endsAt).getTime();
    return evStart <= slotStart.getTime() && evEnd >= slotEnd.getTime();
  });

  let status: SlotStatus;
  let title: string | undefined;
  let eventId: string | undefined;
  let bookingId: string | undefined;

  if (event && (event.kind === 'event' || event.kind === 'taken' || event.kind === 'closed')) {
    status = event.kind === 'closed' ? 'closed' : (event.kind === 'taken' ? 'taken' : 'event');
    title = event.title[getLocale()] || event.title.hu;
    eventId = event.id;
  } else if (timeInPast(slotStart.toISOString())) {
    status = 'closed';
  } else {
    // Anything with an `open` admin event for that window stays open
    // (already handled above). With no event, default is "open" so
    // members can request a slot.
    status = 'open';
  }

  return {
    startsAt: slotStart.toISOString(),
    endsAt: slotEnd.toISOString(),
    status,
    title,
    eventId,
    bookingId,
  };
}

// ---- DOM helpers (no innerHTML for untrusted content) ----
function el<K extends keyof HTMLElementTagNameMap>(tag: K, attrs: Record<string, unknown> = {}, ...kids: Array<Node | string | null | undefined | false>): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  for (const [k, v] of Object.entries(attrs)) {
    if (k === 'class') node.className = String(v);
    else if (k === 'dataset') Object.assign(node.dataset, v as DOMStringMap);
    else if (k.startsWith('on') && typeof v === 'function') node.addEventListener(k.slice(2), v as EventListener);
    else if (v !== undefined && v !== null) node.setAttribute(k, String(v));
  }
  for (const k of kids) if (k) node.append(k);
  return node;
}

// ---- public API ----
export async function mountAgendaApp(): Promise<void> {
  const root = document.getElementById('agendaGrid');
  if (!root) return;
  state.user = getCachedUser();

  document.getElementById('weekPrev')?.addEventListener('click', () => {
    state.weekStart = addDays(state.weekStart, -7);
    render();
  });
  document.getElementById('weekNext')?.addEventListener('click', () => {
    state.weekStart = addDays(state.weekStart, 7);
    render();
  });
  document.getElementById('weekToday')?.addEventListener('click', () => {
    state.weekStart = startOfWeek(new Date());
    render();
  });
  document.querySelectorAll<HTMLButtonElement>('.agenda-app__pill').forEach(btn => {
    btn.addEventListener('click', () => {
      const loc = btn.dataset.loc as 'A22' | 'M15';
      state.location = loc;
      document.querySelectorAll<HTMLButtonElement>('.agenda-app__pill').forEach(b => {
        const on = b.dataset.loc === loc;
        b.classList.toggle('is-active', on);
        b.setAttribute('aria-selected', String(on));
      });
      render();
    });
  });

  await load();
  render();

  // When the user switches locale via the navbar, this agenda was
  // rendered with textContent containing the previous locale's
  // strings. Re-render so labels switch too.
  onLocaleChange(() => { render(); });
}

async function load(): Promise<void> {
  try {
    state.events = await fetchAgendaEvents();
  } catch {
    state.events = [];
  }
  // Public-taken slots (other trainees' approved bookings). Loaded
  // for everyone, logged in or not, so the calendar accurately shows
  // which hours are unavailable.
  state.takenSlots = await fetchTakenSlots().catch(() => []);
  state.bookings = state.user ? await fetchMyBookings().catch(() => []) : [];
}

function render(): void {
  const grid = document.getElementById('agendaGrid');
  const range = document.getElementById('agendaRange');
  if (!grid) return;
  grid.setAttribute('aria-busy', 'false');

  const locale = getLocale() as 'hu' | 'en';
  if (range) {
    range.textContent = formatRangeLabel(state.weekStart, locale);
  }

  grid.replaceChildren();

  // Build a header strip with current date / location meta.
  grid.appendChild(buildHeader());

  // Build the slot grid.
  grid.appendChild(buildGrid(locale));

  // My-bookings card.
  grid.appendChild(buildMyBookings());

  renderMyBookings();
}

function buildHeader(): HTMLElement {
  const locale = getLocale();
  const today = new Date();
  const todayStr = today.toLocaleDateString(locale, { weekday: 'long', month: 'long', day: 'numeric' });

  const header = el('div', { class: 'agenda-app__meta reveal' },
    el('span', { class: 'agenda-app__today' }, todayStr),
  );

  return header;
}

function buildGrid(_locale: 'hu' | 'en'): HTMLElement {
  const wrap = el('div', { class: 'agenda-app__cal' });

  // Day-of-week strip + location pills.
  const headStrip = el('div', { class: 'agenda-app__week' });
  const daySlots = [] as Array<{ date: Date; idx: number; isToday: boolean; iso: string }>;
  const today = new Date();
  for (let d = 0; d < 7; d++) {
    const date = addDays(state.weekStart, d);
    daySlots.push({ date, idx: d, isToday: sameDay(date, today), iso: date.toISOString().slice(0, 10) });
  }

  for (const day of daySlots) {
    const dayBtn = el('button', {
      type: 'button',
      class: 'agenda-app__day' + (day.isToday ? ' is-today' : ''),
      'data-day': String(day.idx),
    },
      el('span', { class: 'agenda-app__day-name' }, t(`agendaApp.days.${dayNameKey(day.idx)}`)),
      el('span', { class: 'agenda-app__day-num' }, String(day.date.getDate())),
      el('span', { class: 'agenda-app__day-mon' }, t(`agendaApp.months.${monthKey(day.date.getMonth())}`)),
    );
    headStrip.appendChild(dayBtn);
  }

  wrap.appendChild(headStrip);

  // Hours list — vertical for each day's selected slot.
  // The grid is now responsive: pick day with the .agenda-app__day pill,
  // then choose an hour from the list. This is far more usable on
  // mobile and cleaner visually than a wide 8×14 table.
  const hoursWrap = el('div', { class: 'agenda-app__hours' });

  // Default day = today within view, else first day.
  let initialDayIdx = daySlots.findIndex(d => d.isToday);
  if (initialDayIdx === -1) initialDayIdx = 0;
  let activeDayIdx = initialDayIdx;

  const hoursList = el('div', { class: 'agenda-app__hour-list' });
  const renderHours = () => {
    hoursList.replaceChildren();
    let hasAny = false;
    for (let h = 0; h < HOUR_COUNT; h++) {
      const cell = classify(activeDayIdx, h);
      const { slotStart } = slotAt(activeDayIdx, h);
      const hour = HOUR_START + h;
      const slotAttrs: Record<string, unknown> = {
        type: 'button',
        class: `agenda-slot agenda-slot--${cell.status}`,
      };
      slotAttrs['data-starts'] = cell.startsAt;
      slotAttrs['data-status'] = cell.status;
      if (cell.eventId) slotAttrs['data-event-id'] = cell.eventId;
      if (cell.bookingId) slotAttrs['data-booking-id'] = cell.bookingId;
      if (cell.title) slotAttrs['title'] = cell.title;

      const btn = el('button', slotAttrs,
        el('span', { class: 'agenda-slot__time' }, `${pad(hour)}:00`),
        el('span', { class: 'agenda-slot__label' }, slotLabel(cell)),
      );
      btn.addEventListener('click', () => handleSlotClick(btn));
      hoursList.appendChild(btn);
      if (cell.status !== 'closed') hasAny = true;
      void slotStart;
    }
    const empty = el('p', { class: 'agenda__empty', hidden: hasAny }, t('agendaApp.empty'));
    hoursList.appendChild(empty);
  };

  headStrip.querySelectorAll<HTMLButtonElement>('.agenda-app__day').forEach(btn => {
    btn.addEventListener('click', () => {
      headStrip.querySelectorAll<HTMLButtonElement>('.agenda-app__day').forEach(b => b.classList.toggle('is-active', b === btn));
      activeDayIdx = Number(btn.dataset.day);
      renderHours();
    });
  });

  // Mark the initial day active.
  const initialBtn = headStrip.querySelectorAll<HTMLButtonElement>('.agenda-app__day')[initialDayIdx];
  if (initialBtn) initialBtn.classList.add('is-active');

  hoursWrap.appendChild(hoursList);
  wrap.appendChild(hoursWrap);

  renderHours();
  return wrap;
}

function slotLabel(cell: SlotCell): string {
  switch (cell.status) {
    case 'open': return t('agendaApp.legend.open');
    case 'event': return cell.title || t('agendaApp.legend.event');
    case 'closed': return '–';
    case 'taken': return t('agendaApp.legend.taken');
    case 'pending': return t('agendaApp.legend.pending');
    case 'mine': return t('agendaApp.legend.mine');
  }
}

// ---- slot interactions ----
function handleSlotClick(btn: HTMLButtonElement): void {
  const status = btn.dataset.status as SlotStatus;
  const startsAt = btn.dataset.starts ?? '';
  const eventId = btn.dataset.eventId ?? '';

  if (status === 'closed') {
    flash(btn);
    return;
  }
  if (status === 'event' || status === 'taken') {
    showInfoModal(btn, status);
    return;
  }
  if (!state.user) {
    showLoginRequired();
    return;
  }
  if (status === 'pending' || status === 'mine') {
    // Show the existing booking info for this user.
    showInfoModal(btn, 'mine');
    return;
  }
  openRequestModal(startsAt, eventId);
}

function flash(btn: HTMLButtonElement): void {
  btn.animate(
    [{ transform: 'translateX(-2px)' }, { transform: 'translateX(2px)' }, { transform: 'translateX(0)' }],
    { duration: 180 },
  );
}

function showInfoModal(btn: HTMLButtonElement, status: SlotStatus): void {
  const title = btn.title || '';
  const back = el('div', { class: 'modal-backdrop' });
  const modal = el('div', { class: 'modal' },
    el('div', { class: 'modal__head' },
      el('h3', { class: 'modal__title' }, status === 'event'
        ? t('agendaApp.legend.event')
        : t('agendaApp.request.title')),
      el('button', { class: 'modal__close', type: 'button', 'aria-label': 'Close' }, '×'),
    ),
    el('div', { class: 'modal__body' },
      el('p', { class: 'modal__text' }, title || '–'),
    ),
    el('div', { class: 'modal__foot' },
      el('button', { class: 'btn btn--ghost', type: 'button', 'data-close': '' }, t('admin.common.cancel')),
    ),
  );
  back.appendChild(modal);
  document.body.appendChild(back);
  const close = () => back.remove();
  back.addEventListener('click', (e) => { if (e.target === back) close(); });
  back.querySelectorAll<HTMLElement>('[data-close], .modal__close').forEach(b => b.addEventListener('click', close));
}

function showLoginRequired(): void {
  const back = el('div', { class: 'modal-backdrop' });
  const loginHref = getLocale() === 'en' ? '/en/login' : '/hu/login';
  back.appendChild(el('div', { class: 'modal' },
    el('div', { class: 'modal__head' },
      el('h3', { class: 'modal__title' }, t('agendaApp.loginRequired')),
      el('button', { class: 'modal__close', type: 'button', 'aria-label': 'Close' }, '×'),
    ),
    el('div', { class: 'modal__foot' },
      el('button', { class: 'btn btn--ghost', type: 'button', 'data-close': '' }, t('admin.common.cancel')),
      el('a', { class: 'btn btn--primary', href: loginHref }, t('agendaApp.loginCta')),
    ),
  ));
  document.body.appendChild(back);
  const close = () => back.remove();
  back.addEventListener('click', (e) => { if (e.target === back) close(); });
  back.querySelectorAll<HTMLElement>('[data-close], .modal__close').forEach(b => b.addEventListener('click', close));
}

function openRequestModal(startsAtIso: string, eventId: string): void {
  const locale = getLocale() as 'hu' | 'en';
  const startDate = new Date(startsAtIso);
  const seedDow = (startDate.getDay() + 6) % 7; // Mon=0..Sun=6

  // ---- recurring state (closed over by the handlers below) ----
  const MAX_WEEKS = 12;
  const MAX_OCCURRENCES = 60;
  let weekdays = new Set<number>([seedDow]);
  let weeks = 4;
  let errorMsg: string | null = null;

  // ---- session types (loaded async; the modal renders an empty select
  //      until they arrive, then we fill in the options + preselect). ----
  const sessionTypeSelect = el('select', { id: 'reqSessionType' }) as HTMLSelectElement;
  const noTypesHint = el('p', { class: 'field__help', hidden: true }, t('agendaApp.request.noSessionTypes'));
  fetchSessionTypesForMember()
    .then(list => {
      sessionTypeSelect.replaceChildren(
        ...list.map(st => el('option', {
          value: st.id,
          'data-price-huf': String(st.priceHuf),
        },
          `${st.name[locale] || st.name.hu} — ${formatHuf(st.priceHuf)}`,
        )),
      );
      if (list.length === 0) {
        noTypesHint.hidden = false;
        sessionTypeSelect.disabled = true;
      } else {
        // Default-select the first type so the modal always submits a
        // valid sessionTypeId. Users can change it from the dropdown.
        sessionTypeSelect.value = list[0].id;
      }
    })
    .catch(() => {
      noTypesHint.hidden = false;
      sessionTypeSelect.disabled = true;
    });

  // ---- DOM scaffolding ----
  const back = el('div', { class: 'modal-backdrop' });

  const recurringInput = el('input', { type: 'checkbox', id: 'reqRecurring' }) as HTMLInputElement;
  const recurringLabelText = el('span', { class: 'switch__label' }, t('agendaApp.request.recurring'));
  const recurringSwitch = el('label', { class: 'switch switch--block', for: 'reqRecurring' },
    recurringInput,
    el('span', { class: 'switch__slider', 'aria-hidden': 'true' }),
    recurringLabelText,
  );

  const recurringPanel = el('div', { class: 'recurring-panel', id: 'recurringPanel', hidden: true });

  // 7 weekday chips, pre-selecting the clicked slot's day.
  const chipEls: HTMLButtonElement[] = [];
  const chipRow = el('div', { class: 'weekday-chips', role: 'group', 'aria-labelledby': 'weekdaysLabel' });
  for (let i = 0; i < 7; i++) {
    const chip = el('button', {
      type: 'button',
      class: 'weekday-chip',
      'data-dow': String(i),
      'aria-pressed': weekdays.has(i) ? 'true' : 'false',
    }, t(`agendaApp.days.${dayNameKey(i)}`)) as HTMLButtonElement;
    chip.addEventListener('click', () => {
      if (weekdays.has(i)) {
        // Don't allow deselecting the last remaining day.
        if (weekdays.size <= 1) return;
        weekdays.delete(i);
      } else {
        weekdays.add(i);
      }
      chip.setAttribute('aria-pressed', weekdays.has(i) ? 'true' : 'false');
      updateSummary();
    });
    chipEls.push(chip);
    chipRow.appendChild(chip);
  }

  const weeksInput = el('input', { type: 'number', id: 'reqWeeks', min: '1', max: String(MAX_WEEKS), value: '4' }) as HTMLInputElement;
  weeksInput.addEventListener('input', () => {
    weeks = Math.min(MAX_WEEKS, Math.max(1, Number(weeksInput.value) || 1));
    updateSummary();
  });

  const summaryEl = el('p', { class: 'recurring-summary', id: 'reqSummary', 'aria-live': 'polite' });

  recurringPanel.appendChild(el('label', { class: 'field__label', id: 'weekdaysLabel', for: 'reqWeeks' }, t('agendaApp.request.weekdaysLabel')));
  recurringPanel.appendChild(el('p', { class: 'field__help' }, t('agendaApp.request.weekdaysHelp')));
  recurringPanel.appendChild(chipRow);
  recurringPanel.appendChild(el('label', { class: 'field__label', for: 'reqWeeks' }, t('agendaApp.request.countLabel')));
  recurringPanel.appendChild(weeksInput);
  recurringPanel.appendChild(summaryEl);

  const notesInput = el('textarea', { id: 'reqNotes', rows: '3' }) as HTMLTextAreaElement;

  const introText = el('p', { class: 'modal__text modal__text--lg' });
  introText.appendChild(el('strong', {}, formatDateTime(startDate, locale)));
  introText.appendChild(document.createTextNode(` — ${state.location}`));

  const submitBtn = el('button', { class: 'btn btn--primary', type: 'button', id: 'reqSubmit' },
    el('span', {}, t('agendaApp.request.submit')),
  ) as HTMLButtonElement;

  const modal = el('div', { class: 'modal' },
    el('div', { class: 'modal__head' },
      el('h3', { class: 'modal__title' }, t('agendaApp.request.title')),
      el('button', { class: 'modal__close', type: 'button', 'aria-label': 'Close' }, '×'),
    ),
    el('div', { class: 'modal__body' },
      introText,
      el('div', { class: 'field' },
        el('label', { for: 'reqSessionType' }, t('agendaApp.request.sessionType')),
        sessionTypeSelect,
        noTypesHint,
      ),
      recurringSwitch,
      recurringPanel,
      el('div', { class: 'field' },
        el('label', { for: 'reqNotes' }, t('agendaApp.request.notes')),
        notesInput,
      ),
    ),
    el('div', { class: 'modal__foot' },
      el('button', { class: 'btn btn--ghost', type: 'button', 'data-close': '' }, t('admin.common.cancel')),
      submitBtn,
    ),
  );
  back.appendChild(modal);

  document.body.appendChild(back);

  // ---- summary builder ----
  function updateSummary(): void {
    const total = weeks * weekdays.size;
    if (total > MAX_OCCURRENCES) {
      errorMsg = t('agendaApp.request.tooMany');
      summaryEl.dataset.error = '1';
      summaryEl.textContent = errorMsg;
      submitBtn.disabled = true;
      return;
    }
    errorMsg = null;
    summaryEl.removeAttribute('data-error');
    submitBtn.disabled = false;

    const sorted = Array.from(weekdays).sort((a, b) => a - b);
    const dayList = sorted
      .map(d => t(`agendaApp.days.${dayNameKey(d)}`))
      .join(', ');

    // Find the last occurrence date by walking forward (same algorithm as backend).
    const lastMs = lastOccurrenceMs(startDate, sorted, weeks);
    const last = new Date(lastMs);
    const fmtShort = (d: Date) => d.toLocaleDateString(locale, { month: 'short', day: 'numeric' });

    if (total === 1) {
      summaryEl.textContent = t('agendaApp.request.summaryOne', { when: fmtShort(startDate) });
    } else {
      let base = t('agendaApp.request.summaryMulti', { count: total, when: fmtShort(startDate), until: fmtShort(last) });
      // Replace trailing "– {{until}}" with the live range to keep the line tight.
      base = base.replace(/\s*–\s*[^·]+$/, '');
      base = base.replace(/\s*\{\{until\}\}/, '').trim();
      // Append the live range + weekday list (only when >1 weekday OR as days suffix).
      base = `${base.replace(/\s*–\s*$/, '')} – ${fmtShort(last)}`;
      base += t('agendaApp.request.summaryWeekdays', { days: dayList });
      summaryEl.textContent = base;
    }
  }

  // ---- wire-up ----
  recurringInput.addEventListener('change', () => {
    recurringPanel.hidden = !recurringInput.checked;
    if (recurringInput.checked) {
      // Ensure at least the seed day is in the set after re-opening.
      if (weekdays.size === 0) {
        weekdays.add(seedDow);
        chipEls[seedDow].setAttribute('aria-pressed', 'true');
      }
      updateSummary();
    }
  });

  const close = () => back.remove();
  back.addEventListener('click', (e) => { if (e.target === back) close(); });
  back.querySelectorAll<HTMLElement>('[data-close], .modal__close').forEach(b => b.addEventListener('click', close));

  submitBtn.addEventListener('click', async () => {
    if (errorMsg) return;
    const isRecurring = recurringInput.checked;
    const sorted = Array.from(weekdays).sort((a, b) => a - b);
    const total = isRecurring ? weeks * sorted.length : 1;
    const notes = notesInput.value;
    const sessionTypeId = sessionTypeSelect.value || null;
    submitBtn.disabled = true;
    try {
      await createBooking({
        location: state.location,
        startsAt: startDate.toISOString(),
        eventId: eventId || null,
        sessionTypeId,
        notes,
        recurrenceCount: total,
        recurrenceWeekdays: isRecurring ? sorted : null,
      });
      close();
      await load();
      render();
      toast(isRecurring ? t('agendaApp.request.submittedRecurring', { count: total }) : t('agendaApp.request.submitted'));
    } catch (err) {
      submitBtn.disabled = false;
      const status = (err as { status?: number }).status;
      const msg = status === 409 ? t('agendaApp.request.alreadyRequested')
        : (err as Error).message || t('common.error');
      toast(msg);
    }
  });

  // Initial summary so the user sees it before flipping the switch.
  updateSummary();
}

function buildMyBookings(): HTMLElement {
  const side = el('aside', { class: 'agenda-app__side' });
  side.appendChild(el('h2', { class: 'agenda-app__side-title' }, t('agendaApp.myBookings')));
  const list = el('div', { id: 'myBookings', class: 'agenda-app__bookings' });
  side.appendChild(list);
  return side;
}

function renderMyBookings(): void {
  const root = document.getElementById('myBookings');
  if (!root) return;
  root.replaceChildren();
  if (!state.user) {
    const p = el('p', { class: 'agenda__empty' }, t('agendaApp.loginRequired') + ' ');
    const a = el('a', { href: getLocale() === 'en' ? '/en/login' : '/hu/login' }, t('agendaApp.loginCta'));
    p.appendChild(a);
    root.appendChild(p);
    return;
  }
  if (!state.bookings.length) {
    root.appendChild(el('p', { class: 'agenda__empty' }, t('agendaApp.noBookings')));
    return;
  }
  const locale = getLocale();
  const sorted = state.bookings.slice().sort((a, b) => new Date(b.startsAt).getTime() - new Date(a.startsAt).getTime());
  for (const b of sorted) {
    const article = el('article', { class: `agenda-booking agenda-booking--${b.status}` });
    article.appendChild(el('span', { class: 'agenda-booking__when' }, formatDateTime(new Date(b.startsAt), locale as 'hu' | 'en')));
    const meta = el('span', { class: 'agenda-booking__meta' });
    meta.appendChild(el('strong', {}, b.location));
    meta.appendChild(document.createTextNode(' · ' + t(`agendaApp.status.${b.status}`)));
    if (b.recurrenceCount > 1) {
      const days = Array.isArray(b.recurrenceWeekdays) && b.recurrenceWeekdays.length
        ? ' · ' + b.recurrenceWeekdays.slice().sort((a, c) => a - c)
            .map(d => t(`agendaApp.days.${dayNameKey(d)}`)).join(', ')
        : '';
      meta.appendChild(document.createTextNode(' · ' + t('admin.requests.recurring', { count: b.recurrenceCount }) + days));
    }
    article.appendChild(meta);
    if (b.decisionNote) {
      article.appendChild(el('p', { class: 'booking__note' }, b.decisionNote));
    }
    if (b.status === 'pending' || b.status === 'approved') {
      const cancelBtn = el('button', { class: 'btn btn--ghost btn--small', type: 'button', 'data-cancel': b.id }, t('agendaApp.cancelBooking'));
      cancelBtn.addEventListener('click', async () => {
        cancelBtn.disabled = true;
        try {
          await cancelBooking(b.id);
          await load();
          render();
        } catch (err) {
          cancelBtn.disabled = false;
          toast((err as Error).message);
        }
      });
      article.appendChild(cancelBtn);
    }
    root.appendChild(article);
  }
}

function toast(msg: string): void {
  document.querySelectorAll('.toast').forEach(n => n.remove());
  const t = el('div', { class: 'toast' }, msg);
  document.body.appendChild(t);
  setTimeout(() => t.remove(), 2400);
}
